import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { API_BASE_URL } from '../config/api';
import SuperAdminSidebar from '../components/SuperAdminSidebar';
import AdminTopBar from '../components/AdminTopBar';

const SuperAdminPage = () => {
  const [institutions, setInstitutions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [unclaimedPrograms, setUnclaimedPrograms] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token || role !== 'superuser') {
      window.location.replace('/');
      return;
    }
    const fetchAll = async () => {
      try {
        setError('');
        setLoading(true);
        const headers = { Authorization: `Bearer ${token}` };
        const [pRes, dRes, iRes, uRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/super/programs`, { headers }),
          fetch(`${API_BASE_URL}/api/super/directors`, { headers }),
          fetch(`${API_BASE_URL}/api/super/institutions`, { headers }),
          fetch(`${API_BASE_URL}/api/super/programs/unclaimed`, { headers })
        ]);
        if (!pRes.ok || !dRes.ok || !iRes.ok || !uRes.ok) throw new Error('Failed to load data');
        const [p, d, i, u] = await Promise.all([pRes.json(), dRes.json(), iRes.json(), uRes.json()]);
        setPrograms(p);
        setDirectors(d);
        setInstitutions(i);
        setUnclaimedPrograms(u);
      } catch (e) {
        setError(e.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const breadcrumbItems = [
    { href: '/super/dashboard', label: 'Super Admin' },
    { label: 'Dashboard' }
  ];

  const totalUsers = institutions.reduce((s, i) => s + (i.userCount ?? 0), 0);

  const statCards = [
    { label: 'Institutions', count: institutions.length, icon: 'bi-building', color: 'primary', href: '/super/accounts' },
    { label: 'Programs', count: programs.length, icon: 'bi-mortarboard', color: 'success', href: '/super/accounts' },
    { label: 'Unclaimed Programs', count: unclaimedPrograms.length, icon: 'bi-exclamation-triangle', color: 'warning', href: '/super/accounts' },
    { label: 'Directors', count: directors.length, icon: 'bi-person-badge', color: 'info', href: '/super/accounts' },
    { label: 'Total Users', count: totalUsers, icon: 'bi-people', color: 'secondary', href: '/super/accounts' }
  ];

  return (
    <div className="d-flex vh-100 overflow-hidden bg-light">
      <SuperAdminSidebar />
      <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto">
        <AdminTopBar variant="super" breadcrumbItems={breadcrumbItems} />
        <div className="flex-grow-1 p-4">
          <Container fluid>
            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
            {loading ? (
              <div className="d-flex align-items-center"><Spinner size="sm" className="me-2" /> Loading...</div>
            ) : (
              <>
                <h4 className="mb-4">Account Overview</h4>
                <Row xs={1} md={2} lg={3} xl={5} className="g-3">
                  {statCards.map((stat) => (
                    <Col key={stat.label}>
                      <Card className="h-100 border-0 shadow-sm stat-card-modern" style={{ transition: 'transform 0.2s' }}>
                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <i className={`bi ${stat.icon} fs-4 text-${stat.color}`}></i>
                            <span className="fs-2 fw-bold">{stat.count}</span>
                          </div>
                          <Card.Title className="small mb-auto">{stat.label}</Card.Title>
                          <Button variant={`outline-${stat.color}`} size="sm" href={stat.href} className="mt-2">
                            View
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
                {unclaimedPrograms.length > 0 && (
                  <Alert variant="warning" className="mt-4">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>{unclaimedPrograms.length}</strong> program{unclaimedPrograms.length !== 1 ? 's' : ''} waiting to be claimed. <a href="/super/accounts">Manage accounts</a>
                  </Alert>
                )}
              </>
            )}
          </Container>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPage;
